import React, { useState, useRef } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { UpdateTemplateContent, GeneratePDF, UploadEditorImage } from '../../../services/AdminService';
import { useParams, useNavigate } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { FaSave, FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
    DecoupledEditor,
    Alignment,
    Autoformat,
    AutoImage,
    AutoLink,
    Autosave,
    BalloonToolbar,
    BlockQuote,
    BlockToolbar,
    Bold,
    Bookmark,
    // Code,
    // CodeBlock,
    Essentials,
    FindAndReplace,
    FontBackgroundColor,
    FontColor,
    FontFamily,
    FontSize,
    FullPage,
    Fullscreen,
    // GeneralHtmlSupport,
    Heading,
    Highlight,
    HorizontalLine,
    // HtmlComment,
    // HtmlEmbed,
    ImageBlock,
    ImageCaption,
    ImageEditing,
    ImageInline,
    ImageInsertViaUrl,
    ImageResize,
    ImageStyle,
    ImageToolbar,
    ImageUpload,
    ImageUtils,
    Indent,
    IndentBlock,
    Italic,
    Link,
    LinkImage,
    List,
    ListProperties,
    MediaEmbed,
    PageBreak,
    Paragraph,
    PasteFromOffice,
    PlainTableOutput,
    RemoveFormat,
    SpecialCharacters,
    SpecialCharactersArrows,
    SpecialCharactersCurrency,
    SpecialCharactersEssentials,
    SpecialCharactersLatin,
    SpecialCharactersMathematical,
    SpecialCharactersText,
    Strikethrough,
    // Style,
    Subscript,
    Superscript,
    Table,
    TableCaption,
    TableCellProperties,
    TableColumnResize,
    TableLayout,
    TableProperties,
    TableToolbar,
    TextPartLanguage,
    TextTransformation,
    TodoList,
    Underline,
    WordCount
} from 'ckeditor5';
import { SourceEditingEnhanced } from 'ckeditor5-premium-features';
import 'ckeditor5/ckeditor5.css';
import 'ckeditor5-premium-features/ckeditor5-premium-features.css';
// import './Template.css';

const LICENSE_KEY = process.env.REACT_APP_CKEDITOR_LICENSE_KEY;

function CustomUploadAdapter(editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
        return {
            upload: async () => {
                const file = await loader.file;
                const formData = new FormData();
                formData.append('upload', file);

                try {
                    const response = await UploadEditorImage(formData);
                    if (response.data.uploaded && response.data.url) {
                        return { default: response.data.url };
                    }
                    throw new Error('Upload failed');
                } catch (error) {
                    throw new Error('Upload failed: ' + (error.response?.data?.error?.message || error.message));
                }
            },
            abort: () => { }
        };
    };
}

const TemplateEditor = ({ htmlContent = '' }) => {
    const { templateUuid } = useParams();
    const navigate = useNavigate();
    const [content, setContent] = useState(htmlContent);
    const [loading, setLoading] = useState(false);
    const editorContainerRef = useRef(null);
    const editorMenuBarRef = useRef(null);
    const editorToolbarRef = useRef(null);
    const editorWordCountRef = useRef(null);
    const editorRef = useRef(null);

    const editorConfig = {
        toolbar: {
            items: [
                'undo',
                'redo',
                '|',
                'sourceEditingEnhanced',
                '|',
                'heading',
                // 'style',
                '|',
                'fontSize',
                'fontFamily',
                'fontColor',
                'fontBackgroundColor',
                '|',
                'bold',
                'italic',
                'underline',
                '|',
                'link',
                'insertTable',
                'insertTableLayout',
                'highlight',
                'blockQuote',
                // 'codeBlock',
                '|',
                'alignment',
                '|',
                'bulletedList',
                'numberedList',
                'todoList',
                'outdent',
                'indent'
            ],
            shouldNotGroupWhenFull: false
        },
        plugins: [
            Alignment,
            Autoformat,
            AutoImage,
            AutoLink,
            Autosave,
            BalloonToolbar,
            BlockQuote,
            BlockToolbar,
            Bold,
            Bookmark,
            // Code,
            // CodeBlock,
            Essentials,
            FindAndReplace,
            FontBackgroundColor,
            FontColor,
            FontFamily,
            FontSize,
            FullPage,
            Fullscreen,
            // GeneralHtmlSupport,
            Heading,
            Highlight,
            HorizontalLine,
            // HtmlComment,
            // HtmlEmbed,
            ImageBlock,
            ImageCaption,
            ImageEditing,
            ImageInline,
            ImageInsertViaUrl,
            ImageResize,
            ImageStyle,
            ImageToolbar,
            ImageUpload,
            ImageUtils,
            Indent,
            IndentBlock,
            Italic,
            Link,
            LinkImage,
            List,
            ListProperties,
            MediaEmbed,
            PageBreak,
            Paragraph,
            PasteFromOffice,
            PlainTableOutput,
            RemoveFormat,
            SourceEditingEnhanced,
            SpecialCharacters,
            SpecialCharactersArrows,
            SpecialCharactersCurrency,
            SpecialCharactersEssentials,
            SpecialCharactersLatin,
            SpecialCharactersMathematical,
            SpecialCharactersText,
            Strikethrough,
            // Style,
            Subscript,
            Superscript,
            Table,
            TableCaption,
            TableCellProperties,
            TableColumnResize,
            TableLayout,
            TableProperties,
            TableToolbar,
            TextPartLanguage,
            TextTransformation,
            TodoList,
            Underline,
            WordCount,
            CustomUploadAdapter
        ],
        balloonToolbar: ['bold', 'italic', '|', 'link', '|', 'bulletedList', 'numberedList'],
        blockToolbar: [
            'fontSize',
            'fontColor',
            'fontBackgroundColor',
            '|',
            'bold',
            'italic',
            '|',
            'link',
            'insertTable',
            'insertTableLayout',
            '|',
            'bulletedList',
            'numberedList',
            'outdent',
            'indent'
        ],
        fontFamily: {
            supportAllValues: true
        },
        fontSize: {
            options: [10, 12, 14, 'default', 18, 20, 22],
            supportAllValues: true
        },
        fullscreen: {
            onEnterCallback: container =>
                container.classList.add(
                    'editor-container',
                    'editor-container_document-editor',
                    'editor-container_include-style',
                    'editor-container_include-word-count',
                    'editor-container_include-fullscreen',
                    'main-container'
                )
        },
        heading: {
            options: [
                { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
                { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
                { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
            ]
        },
        htmlSupport: {
            allow: [
                {
                    name: /^(div|p|span|h[1-6]|ul|ol|li|a|img|table|tr|td|th)$/,
                    styles: ['text-align', 'margin', 'padding', 'font-size', 'font-family', 'color', 'background-color'],
                    attributes: true,
                    classes: true
                }
            ]
        },
        image: {
            toolbar: ['toggleImageCaption', '|', 'imageStyle:inline', 'imageStyle:wrapText', 'imageStyle:breakText', '|', 'resizeImage'],
            upload: {
                types: ['jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff']
            }
        },
        licenseKey: LICENSE_KEY,
        link: {
            addTargetToExternalLinks: true,
            defaultProtocol: 'https://',
            decorators: {
                toggleDownloadable: {
                    mode: 'manual',
                    label: 'Downloadable',
                    attributes: {
                        download: 'file'
                    }
                }
            }
        },
        list: {
            properties: {
                styles: true,
                startIndex: true,
                reversed: true
            }
        },
        // style: {
        //     definitions: [
        //         { name: 'Article category', element: 'h3', classes: ['category'] },
        //         { name: 'Title', element: 'h2', classes: ['document-title'] },
        //         { name: 'Subtitle', element: 'h3', classes: ['document-subtitle'] },
        //         { name: 'Info box', element: 'p', classes: ['info-box'] },
        //         { name: 'CTA Link Primary', element: 'a', classes: ['button', 'button--green'] },
        //         { name: 'CTA Link Secondary', element: 'a', classes: ['button', 'button--black'] },
        //         { name: 'Marker', element: 'span', classes: ['marker'] },
        //         { name: 'Spoiler', element: 'span', classes: ['spoiler'] }
        //     ]
        // },
        table: {
            contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
        }
    };
    // const handleGeneratePDF = async () => {
    //     if (!content) {
    //         toast.error('Content cannot be empty');
    //         return;
    //     }

    //     setLoading(true);

    //     try {
    //         const response = await GeneratePDF({
    //             html_content: content
    //         });

    //         // Create a blob URL for the PDF
    //         const url = window.URL.createObjectURL(new Blob([response.data]));
    //         const link = document.createElement('a');
    //         link.href = url;
    //         link.setAttribute('download', 'template.pdf');
    //         document.body.appendChild(link);
    //         link.click();
    //         link.parentNode.removeChild(link);

    //         toast.success('PDF generated successfully');
    //     } catch (err) {
    //         toast.error(err.response?.data?.message || 'Error generating PDF');
    //     } finally {
    //         setLoading(false);
    //     }
    // };


    const updateLocalStorageContent = (templateUuid, newHtmlContent) => {
        try {
            const categoriesData = JSON.parse(localStorage.getItem("categoriesData") || "[]");
            for (const category of categoriesData) {
                const template = (category.templates || []).find(tpl => String(tpl.uuid) === String(templateUuid));
                if (template) {
                    template.html_content = newHtmlContent;
                    break;
                }
            }
            localStorage.setItem("categoriesData", JSON.stringify(categoriesData));
        } catch (err) {
            console.error('Error updating localStorage:', err);
        }
    };

    const handleSaveAndNext = async () => {
        setLoading(true);
        if (!content) {
            toast.error('Content cannot be empty');
            return;
        }

        try {
            await UpdateTemplateContent({
                template_uuid: templateUuid,
                html_content: content
            });
            updateLocalStorageContent(templateUuid, content);

            toast.success('Template saved successfully');
            navigate(`/back-admin/template/${templateUuid}/edit-questions`);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error saving template content');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {loading && (
                <div className="page-loading">
                    <img src="/images/loader.gif" alt="loader" className="page-loader" />
                </div>
            )}
            <div className="main-container" style={{
                minHeight: 'calc(100vh - 54.5px)'
            }}>
                <div
                    className="editor-container editor-container_document-editor editor-container_include-style editor-container_include-word-count editor-container_include-fullscreen"
                    ref={editorContainerRef}
                >
                    <div className="editor-container__menu-bar" ref={editorMenuBarRef}></div>
                    <div className="editor-container__toolbar" ref={editorToolbarRef}></div>
                    <div className="editor-container__editor-wrapper">
                        <div
                            className="editor-container__editor d-flex justify-content-center py-4"
                            style={{ background: '#f0f0f0' }}
                        >
                            <div ref={editorRef} className="w-100">
                                <CKEditor
                                    editor={DecoupledEditor}
                                    config={editorConfig}
                                    data={content}
                                    onReady={editor => {
                                        const wordCount = editor.plugins.get('WordCount');
                                        editorWordCountRef.current.appendChild(wordCount.wordCountContainer);
                                        editorToolbarRef.current.appendChild(editor.ui.view.toolbar.element);
                                        editorMenuBarRef.current.appendChild(editor.ui.view.menuBarView.element);
                                        editorRef.current = editor;
                                    }}
                                    onChange={(event, editor) => {
                                        const data = editor.getData();
                                        setContent(data);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className='admin-editor-footer'>
                    <div className="admin-editor-word-count" ref={editorWordCountRef}></div>
                    <div className="admin-editor-actions">
                        <button
                            className="admin-btn admin-btn-secondary"
                            onClick={() => navigate('/back-admin/template')}
                            disabled={loading}
                        >
                            <FaArrowLeft /> Back
                        </button>
                        <button
                            className="admin-btn admin-btn-primary"
                            onClick={handleSaveAndNext}
                            disabled={loading}
                        >
                            <FaSave /> Save
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TemplateEditor;